// Sketches: design alternatives kept in the browser. Each edit makes a new document; the previous
// ones are kept for undo. Documents are saved to IndexedDB shortly after they change.
import { branchDocument, canonicalJson, EditError, hash53, type EfModelDocument } from '@modelviewer/core';
import { create } from 'zustand';
import { LAYOUTS, layoutStorageKey, SPACINGS } from '../diagram/layouts';
import { db, type SketchBase, type SketchRecord } from '../lib/db';
import { baseFromDoc } from './folderItems';
import { dropSketch, findSet, loadModel, putSketch, useWorkspace, type ModelRef } from './workspace';
import { toast } from './toast';

const HISTORY_LIMIT = 200;

interface History {
  past: EfModelDocument[];
  future: EfModelDocument[];
}

/** Undo and redo stacks per sketch (in memory: they last as long as the page). */
export const useSketchHistory = create<Record<string, History>>(() => ({}));

const saveTimers = new Map<string, number>();

function save(record: SketchRecord): void {
  window.clearTimeout(saveTimers.get(record.id));
  saveTimers.set(
    record.id,
    window.setTimeout(() => {
      saveTimers.delete(record.id);
      void db.sketches.put(record);
    }, 300),
  );
}

/** Saves pending changes now (before a download or when the page is hidden). */
export async function flushSketches(): Promise<void> {
  const pending = [...saveTimers.keys()];
  for (const id of pending) {
    window.clearTimeout(saveTimers.get(id));
    saveTimers.delete(id);
    const record = useWorkspace.getState().sketches[id];
    if (record) await db.sketches.put(record);
  }
}

if (typeof window !== 'undefined') window.addEventListener('pagehide', () => void flushSketches());

export function getSketch(id: string): SketchRecord | undefined {
  return useWorkspace.getState().sketches[id];
}

/** A title not used by another sketch of this folder. */
function freeTitle(wanted: string): string {
  const taken = new Set(Object.values(useWorkspace.getState().sketches).map((s) => s.doc.title));
  let title = wanted;
  for (let i = 2; taken.has(title); i++) title = `${wanted} ${i}`;
  return title;
}

function describeBase(ref: ModelRef): string {
  if (ref.sketchId) return getSketch(ref.sketchId)?.doc.title ?? 'sketch';
  const set = findSet(ref.setKey);
  if (!ref.migrationId) return set?.contextName ?? 'model';
  return set?.migrations.find((m) => m.id === ref.migrationId)?.name ?? ref.migrationId;
}

async function store(record: SketchRecord): Promise<SketchRecord> {
  putSketch(record);
  await db.sketches.put(record);
  return record;
}

/** Starts a sketch from a model of the folder or from another sketch. */
export async function branchSketch(from: ModelRef, title?: string): Promise<SketchRecord | null> {
  const source = useWorkspace.getState().source;
  const loaded = await loadModel(from);
  if (!source || !loaded) return null;
  const parent = from.sketchId ? getSketch(from.sketchId) : undefined;
  const set = parent ? (parent.setKey ? findSet(parent.setKey) : undefined) : findSet(from.setKey);
  const basedOn: NonNullable<EfModelDocument['basedOn']> = {
    ...(set ? { context: set.contextName } : {}),
    ...(from.migrationId ? { migration: from.migrationId } : {}),
    ...(parent ? { sketch: parent.doc.title } : {}),
    modelHash: hash53(canonicalJson(loaded.result.doc.model)),
  };
  const doc = branchDocument(loaded.result.doc, freeTitle(title ?? `Idea from ${describeBase(from)}`), basedOn);
  const now = Date.now();
  const base: SketchBase = { setKey: from.setKey, migrationId: from.migrationId, ...(from.sketchId ? { sketchId: from.sketchId } : {}) };
  const record = await store({ id: crypto.randomUUID(), sourceId: source.id, setKey: parent?.setKey ?? (set ? set.key : null), base, doc, created: now, updated: now });
  await copyLayout(from, record.id);
  return record;
}

/** Adds a sketch from an efmodel document (a file, the clipboard or an agent's output). */
export async function importSketch(doc: EfModelDocument, options: { base?: ModelRef | null; setKey?: string | null } = {}): Promise<SketchRecord | null> {
  const source = useWorkspace.getState().source;
  if (!source) return null;
  const { discovery, sketches } = useWorkspace.getState();
  const found = baseFromDoc(doc, discovery, sketches);
  const setKey = options.setKey !== undefined ? options.setKey : found.setKey;
  const base = options.base !== undefined ? options.base : found.base;
  const now = Date.now();
  return store({ id: crypto.randomUUID(), sourceId: source.id, setKey, base, doc: { ...doc, title: freeTitle(doc.title) }, created: now, updated: now });
}

export async function duplicateSketch(id: string): Promise<SketchRecord | null> {
  const sketch = getSketch(id);
  if (!sketch) return null;
  const now = Date.now();
  const { origin: _folder, ...copy } = structuredClone(sketch);
  const title = freeTitle(sketch.origin ? sketch.doc.title : `${sketch.doc.title} copy`);
  const record = await store({ ...copy, id: crypto.randomUUID(), doc: { ...copy.doc, title }, created: now, updated: now });
  await copyLayout({ setKey: sketch.setKey ?? '', migrationId: null, sketchId: id }, record.id);
  return record;
}

export async function deleteSketch(id: string): Promise<void> {
  window.clearTimeout(saveTimers.get(id));
  saveTimers.delete(id);
  dropSketch(id);
  useSketchHistory.setState((s) => {
    const next = { ...s };
    delete next[id];
    return next;
  });
  await db.sketches.delete(id);
}

/** Replaces a sketch's document, keeping the previous one for undo. */
export function setSketchDoc(id: string, doc: EfModelDocument): void {
  const sketch = getSketch(id);
  if (!sketch || sketch.doc === doc) return;
  if (sketch.origin) {
    toast('This sketch is a file in the folder. Duplicate it to edit a copy.', 'error');
    return;
  }
  const history = useSketchHistory.getState()[id] ?? { past: [], future: [] };
  useSketchHistory.setState({ [id]: { past: [...history.past, sketch.doc].slice(-HISTORY_LIMIT), future: [] } });
  const record = { ...sketch, doc, updated: Date.now() };
  putSketch(record);
  save(record);
}

/**
 * Applies an edit to a sketch. Edits that cannot be made (an EditError) are shown as a message
 * and change nothing. Returns whether the edit was made.
 */
export function editSketch(id: string, change: (doc: EfModelDocument) => EfModelDocument): boolean {
  const sketch = getSketch(id);
  if (!sketch) return false;
  if (sketch.origin) {
    toast('This sketch is a file in the folder. Duplicate it to edit a copy.', 'error');
    return false;
  }
  try {
    setSketchDoc(id, change(sketch.doc));
    return true;
  } catch (e) {
    if (e instanceof EditError) {
      toast(e.message, 'error');
      return false;
    }
    throw e;
  }
}

function travel(id: string, direction: 'undo' | 'redo'): void {
  const sketch = getSketch(id);
  const history = useSketchHistory.getState()[id];
  if (!sketch || !history) return;
  const from = direction === 'undo' ? history.past : history.future;
  const doc = from.at(-1);
  if (!doc) return;
  const rest = from.slice(0, -1);
  useSketchHistory.setState({
    [id]: direction === 'undo' ? { past: rest, future: [...history.future, sketch.doc] } : { past: [...history.past, sketch.doc], future: rest },
  });
  const record = { ...sketch, doc, updated: Date.now() };
  putSketch(record);
  save(record);
}

export const undoSketch = (id: string) => travel(id, 'undo');
export const redoSketch = (id: string) => travel(id, 'redo');

// ------------------------------------------------------------------ layouts

const MODES = ['all', 'keys', 'none'];

/** Every stored layout of a model: per property mode, arrangement and spacing. */
const allLayoutKeys = (sourceId: string, ref: ModelRef) =>
  MODES.flatMap((mode) => LAYOUTS.flatMap((l) => SPACINGS.map((s) => layoutStorageKey(layoutKeyFor(sourceId, ref, mode), l.id, s.id))));

/** The key under which a diagram of this model keeps its card positions. */
export function layoutKeyFor(sourceId: string, ref: ModelRef, mode: string): string {
  return ref.sketchId ? `${sourceId}|sketch:${ref.sketchId}|${mode}` : `${sourceId}|${ref.setKey}|${mode}`;
}

/** A new sketch starts with the card positions of the model it came from. */
async function copyLayout(from: ModelRef, sketchId: string): Promise<void> {
  const source = useWorkspace.getState().source;
  if (!source) return;
  const to = allLayoutKeys(source.id, { setKey: '', migrationId: null, sketchId });
  for (const [i, key] of allLayoutKeys(source.id, from).entries()) {
    const saved = await db.layouts.get(key);
    if (saved) await db.layouts.put({ ...saved, key: to[i]!, updated: Date.now() });
  }
}

/** Keeps a renamed entity's card where it was. */
export async function moveLayoutEntry(sketchId: string, from: string, to: string): Promise<void> {
  const source = useWorkspace.getState().source;
  if (!source) return;
  for (const key of allLayoutKeys(source.id, { setKey: '', migrationId: null, sketchId })) {
    const saved = await db.layouts.get(key);
    const position = saved?.positions[from];
    if (!saved || !position) continue;
    const positions = { ...saved.positions, [to]: position };
    delete positions[from];
    await db.layouts.put({ ...saved, positions, updated: Date.now() });
  }
}

export function baseRefOf(sketch: SketchRecord): ModelRef | null {
  return sketch.base ? { setKey: sketch.base.setKey, migrationId: sketch.base.migrationId, ...(sketch.base.sketchId ? { sketchId: sketch.base.sketchId } : {}) } : null;
}

export function baseLabel(sketch: SketchRecord): string | null {
  const base = baseRefOf(sketch);
  if (!base) return null;
  if (base.sketchId) return getSketch(base.sketchId)?.doc.title ?? 'a deleted sketch';
  const set = findSet(base.setKey);
  if (!set) return null;
  return base.migrationId ? `${set.contextName} · ${set.migrations.find((m) => m.id === base.migrationId)?.name ?? base.migrationId}` : `${set.contextName} (current model)`;
}

